import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Navbar } from '../components/Navbar';
import { Footer } from '../components/Footer';
import { BusinessCard } from '../components/BusinessCard';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { 
  Search, 
  ArrowRight, 
  Users, 
  Store, 
  Star, 
  MapPin,
  Utensils,
  ShoppingBag,
  Wrench,
  Briefcase,
  Sparkles,
  Church,
  Compass,
  Ship,
  Palmtree,
  Plane,
  HeartHandshake,
  Building2,
  Heart,
  Camera,
  Video,
  Palette,
  Landmark,
  Clapperboard,
  Music
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const categories = [
  { id: 'Restaurants', name: 'Restaurants', icon: Utensils, description: 'Soul food to fine dining' },
  { id: 'Retail', name: 'Retail', icon: ShoppingBag, description: 'Fashion, books & more' },
  { id: 'Services', name: 'Services', icon: Wrench, description: 'Home & auto services' },
  { id: 'Professional', name: 'Professional', icon: Briefcase, description: 'Legal, medical & finance' },
  { id: 'Beauty & Wellness', name: 'Beauty', icon: Sparkles, description: 'Salons, spas & wellness' },
  { id: 'Churches', name: 'Churches', icon: Church, description: 'Faith & community' },
  { id: 'Non-Profits', name: 'Non-Profits', icon: HeartHandshake, description: 'Community support' },
  { id: 'Foundations', name: 'Foundations', icon: Building2, description: 'Charitable foundations' },
  { id: 'Charities', name: 'Charities', icon: Heart, description: 'Giving back' },
  { id: 'Organizations', name: 'Organizations', icon: Users, description: 'Community groups' },
  { id: 'Photography', name: 'Photography', icon: Camera, description: 'Capture moments' },
  { id: 'Videography', name: 'Videography', icon: Video, description: 'Video production' },
  { id: 'Graphic Design', name: 'Graphic Design', icon: Palette, description: 'Creative designs' },
  { id: 'Museums', name: 'Museums', icon: Landmark, description: 'Art & history' },
  { id: 'Film Production', name: 'Film', icon: Clapperboard, description: 'Movie making' },
  { id: 'Music Recording Company', name: 'Music Recording', icon: Music, description: 'Sound studios' },
  { id: 'Adventures', name: 'Adventures', icon: Compass, description: 'Tours & experiences' },
  { id: 'Cruises', name: 'Cruises', icon: Ship, description: 'Sea voyages' },
  { id: 'Vacation Rentals', name: 'Vacations', icon: Palmtree, description: 'Holiday stays' },
  { id: 'Travel Agencies', name: 'Travel', icon: Plane, description: 'Trip planning' },
];

const HomePage = () => {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [featuredBusinesses, setFeaturedBusinesses] = useState([]);
  const [stats, setStats] = useState({ businesses: 0, reviews: 0, owners: 0, states: 0 });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [businessesRes, statsRes] = await Promise.all([
          axios.get(`${API}/businesses?limit=6`),
          axios.get(`${API}/stats`)
        ]);
        setFeaturedBusinesses(businessesRes.data);
        setStats(statsRes.data);
      } catch (err) {
        if (process.env.NODE_ENV === 'development') {
          console.error('Failed to fetch data:', err);
        }
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/directory?search=${encodeURIComponent(searchQuery)}`);
    } else {
      navigate('/directory');
    }
  };

  return (
    <div className="min-h-screen bg-stone-white" data-testid="home-page">
      <Navbar />

      {/* Hero Section */}
      <section className="relative pt-24 pb-16 md:pt-32 md:pb-24 overflow-hidden">
        {/* Background Pattern */}
        <div className="absolute inset-0 bg-gradient-to-br from-emerald-deep/5 via-transparent to-legacy-gold/5" />
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-4xl mx-auto">
            {/* Badge */}
            <div className="inline-flex items-center gap-2 bg-emerald-deep/10 text-emerald-deep px-4 py-2 rounded-full text-sm font-medium mb-6 animate-fadeIn">
              <Store className="w-4 h-4" />
              <span>Supporting Black Excellence Nationwide</span>
            </div>

            {/* Headline */}
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold text-stone-900 tracking-tight mb-6 animate-slideUp" data-testid="hero-title">
              Discover & Support{' '}
              <span className="text-emerald-deep">Black-Owned</span>{' '}
              Businesses
            </h1>

            {/* Subheadline */}
            <p className="text-lg sm:text-xl text-stone-600 max-w-2xl mx-auto mb-10 animate-slideUp stagger-1">
              Find amazing restaurants, services, and shops owned by Black entrepreneurs 
              across the United States. Shop local, support community.
            </p>

            {/* Quick Stats */}
            <div className="flex flex-wrap justify-center gap-8 mt-4 animate-slideUp stagger-2">
              <div className="flex items-center gap-2 text-stone-600">
                <Store className="w-5 h-5 text-emerald-deep" />
                <span className="font-semibold">{stats.businesses}+</span>
                <span>Businesses</span>
              </div>
              <div className="flex items-center gap-2 text-stone-600">
                <Star className="w-5 h-5 text-legacy-gold" />
                <span className="font-semibold">{stats.reviews}+</span>
                <span>Reviews</span>
              </div>
              <div className="flex items-center gap-2 text-stone-600">
                <MapPin className="w-5 h-5 text-emerald-deep" />
                <span className="font-semibold">{stats.states || 50}</span>
                <span>States</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Categories Section */}
      <section className="py-16 md:py-24 bg-white" data-testid="categories-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8">
            <h2 className="font-display text-3xl md:text-4xl font-bold text-stone-900 mb-4">
              Browse by Category
            </h2>
            <p className="text-stone-600 max-w-xl mx-auto">
              Explore diverse Black-owned businesses across various industries
            </p>
          </div>

          {/* Search Bar - Under Browse by Category */}
          <form onSubmit={handleSearch} className="max-w-2xl mx-auto mb-12">
            <div className="relative flex items-center">
              <Search className="absolute left-5 w-5 h-5 text-stone-400" />
              <Input
                type="text"
                placeholder="Search businesses, categories, or locations..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="h-14 pl-14 pr-36 rounded-full border-stone-200 shadow-lg focus:ring-2 focus:ring-emerald-deep/30 text-lg"
                data-testid="category-search-input"
              />
              <Button
                type="submit"
                className="absolute right-2 bg-emerald-deep hover:bg-emerald-deep/90 text-white rounded-full px-6 h-10"
                data-testid="category-search-btn"
              >
                Search
              </Button>
            </div>
          </form>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
            {categories.map((category) => {
              const Icon = category.icon;
              return (
                <Link
                  key={category.id}
                  to={`/directory?category=${encodeURIComponent(category.id)}`}
                  className="group p-5 bg-stone-50 rounded-2xl hover:bg-emerald-deep hover:shadow-lg transition-all duration-300"
                  data-testid={`category-link-${category.id}`}
                >
                  <div className="w-10 h-10 bg-white rounded-xl flex items-center justify-center mb-3 group-hover:bg-white/20 transition-colors">
                    <Icon className="w-5 h-5 text-emerald-deep group-hover:text-white transition-colors" />
                  </div>
                  <h3 className="font-display text-base font-semibold text-stone-900 group-hover:text-white transition-colors mb-1">
                    {category.name}
                  </h3>
                  <p className="text-xs text-stone-500 group-hover:text-white/80 transition-colors">
                    {category.description}
                  </p>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* Featured Businesses */}
      <section className="py-16 md:py-24" data-testid="featured-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col sm:flex-row sm:items-end justify-between mb-12 gap-4">
            <div>
              <h2 className="font-display text-3xl md:text-4xl font-bold text-stone-900 mb-2">
                Featured Businesses
              </h2>
              <p className="text-stone-600">
                Discover top-rated Black-owned businesses in your area
              </p>
            </div>
            <Link to="/directory">
              <Button variant="ghost" className="text-emerald-deep hover:text-emerald-deep/80 gap-2" data-testid="view-all-btn">
                View All <ArrowRight className="w-4 h-4" />
              </Button>
            </Link>
          </div>

          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[1, 2, 3].map((i) => (
                <div key={i} className="bg-white rounded-xl h-80 animate-pulse" />
              ))}
            </div>
          ) : featuredBusinesses.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {featuredBusinesses.map((business, index) => (
                <BusinessCard
                  key={business.business_id}
                  business={business}
                  featured={index === 0}
                />
              ))}
            </div>
          ) : (
            <div className="text-center py-16 bg-white rounded-2xl">
              <Store className="w-16 h-16 text-stone-300 mx-auto mb-4" />
              <h3 className="font-display text-xl font-semibold text-stone-700 mb-2">
                No Businesses Yet
              </h3>
              <p className="text-stone-500 mb-6">
                Be the first to list your business on our platform
              </p>
              <Link to="/add-business">
                <Button className="bg-emerald-deep hover:bg-emerald-deep/90 text-white rounded-full">
                  Add Your Business
                </Button>
              </Link>
            </div>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 md:py-24 bg-emerald-deep" data-testid="cta-section">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-display text-3xl md:text-4xl font-bold text-white mb-6">
            Own a Black-Owned Business?
          </h2>
          <p className="text-emerald-100/80 text-lg mb-8 max-w-2xl mx-auto">
            Join our growing directory and connect with customers who want to support 
            Black-owned businesses. It's free to list!
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/register">
              <Button className="bg-white text-emerald-deep hover:bg-stone-50 rounded-full px-8 py-6 text-lg font-semibold" data-testid="cta-register-btn">
                Register Your Business
              </Button>
            </Link>
            <Link to="/directory">
              <Button variant="outline" className="border-white text-white hover:bg-white/10 rounded-full px-8 py-6 text-lg font-semibold" data-testid="cta-explore-btn">
                Explore Directory
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 md:py-24 bg-white" data-testid="stats-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="stats-card">
              <Store className="w-10 h-10 text-emerald-deep mx-auto mb-4" />
              <div className="font-display text-4xl font-bold text-stone-900 mb-2">
                {stats.businesses}+
              </div>
              <div className="text-stone-500">Businesses Listed</div>
            </div>
            <div className="stats-card">
              <Users className="w-10 h-10 text-emerald-deep mx-auto mb-4" />
              <div className="font-display text-4xl font-bold text-stone-900 mb-2">
                {stats.owners}+
              </div>
              <div className="text-stone-500">Business Owners</div>
            </div>
            <div className="stats-card">
              <Star className="w-10 h-10 text-legacy-gold mx-auto mb-4" />
              <div className="font-display text-4xl font-bold text-stone-900 mb-2">
                {stats.reviews}+
              </div>
              <div className="text-stone-500">Reviews</div>
            </div>
            <div className="stats-card">
              <MapPin className="w-10 h-10 text-emerald-deep mx-auto mb-4" />
              <div className="font-display text-4xl font-bold text-stone-900 mb-2">
                {stats.states || 50}
              </div>
              <div className="text-stone-500">States Covered</div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default HomePage;
